<template>
  <div :class="containerClasses">
    <!-- Cabecera -->
    <div :class="tabsClasses">
      <ul>
        <li
          v-for="(tab, index) in tabList"
          :key="index"
          :class="getTabClasses(tab)"
          @click="selectTab(tab)"
          :style="tab.disabled ? { cursor: 'not-allowed', opacity: 0.6 } : {}"
        >
          <a :style="getTabStyle(tab)">
            <span v-if="tab.icon" class="icon is-small">
              <i :class="tab.icon"></i>
            </span>
            <span>{{ tab.label }}</span>
            <span
              v-if="tab.badge && tab.badge > 0"
              class="tag is-small is-rounded ml-1"
              :class="getBadgeClass(tab)"
            >
              {{ tab.badge }}
            </span>
          </a>
        </li>
      </ul>
    </div>

    <!-- Contenido -->
    <div class="tab-content" :class="contentClasses">
      <div
        v-for="(tab, index) in tabList"
        :key="index"
        v-show="activeTab === tab.name"
        class="tab-panel"
      >
        <component :is="slots[tab.name]" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "GenericTabs",
  props: {
    // Etiquetas visibles para cada tab
    tabs: {
      type: Array,
      required: true,
      // Ejemplo esperado:
      // [
      //   {
      //     name: "tab1",
      //     label: "General Settings",
      //     icon: "fa fa-cog", // Opcional: icono Font Awesome
      //     color: "primary", // Opcional: primary, info, success, warning, danger
      //     disabled: false, // Opcional: deshabilitar pestaña
      //     badge: 5 // Opcional: número para mostrar badge
      //   },
      //   { name: "tab2", label: "Bulk Process" }
      // ]
    },
    orientation: {
      type: String,
      default: "horizontal",
      validator: (value) => ["horizontal", "vertical"].includes(value),
    },
    // Color tema para las pestañas activas
    theme: {
      type: String,
      default: "primary",
      validator: (value) =>
        ["primary", "info", "success", "warning", "danger", "dark"].includes(
          value
        ),
    },
    // Animación al cambiar pestañas
    animated: {
      type: Boolean,
      default: false,
    },
    // Tamaño de las pestañas
    size: {
      type: String,
      default: "normal",
      validator: (value) =>
        ["small", "normal", "medium", "large"].includes(value),
    },
  },
  data() {
    return {
      activeTab: this.tabs.length ? this.tabs[0].name : null,
    };
  },
  computed: {
    slots() {
      return this.$slots;
    },
    tabList() {
      return this.tabs;
    },
    containerClasses() {
      return {
        "tabs-container": true,
        "tabs-vertical": this.orientation === "vertical",
        "tabs-horizontal": this.orientation === "horizontal",
        "is-animated": this.animated,
      };
    },
    tabsClasses() {
      return {
        tabs: true,
        "is-boxed": this.orientation === "horizontal",
        "is-vertical": this.orientation === "vertical",
        [`is-${this.size}`]: this.size !== "normal",
      };
    },
    contentClasses() {
      return {
        "is-animated": this.animated,
      };
    },
  },
  methods: {
    selectTab(tab) {
      if (tab.disabled) return;
      this.activeTab = tab.name;
      this.$emit("tab-changed", tab);
    },
    getTabClasses(tab) {
      return {
        "is-active": this.activeTab === tab.name,
        "is-disabled": tab.disabled,
        [`has-text-${tab.color}`]: tab.color && this.activeTab !== tab.name,
      };
    },
    getTabStyle(tab) {
      if (this.activeTab === tab.name) {
        const color = tab.color || this.theme;
        return this.orientation === "vertical"
          ? { borderLeftColor: `hsl(${this.getColorHsl(color)})` }
          : { borderBottomColor: `hsl(${this.getColorHsl(color)})` };
      }
      return {};
    },
    getBadgeClass(tab) {
      const color = tab.color || this.theme;
      return `is-${color}`;
    },
    getColorHsl(color) {
      const colors = {
        primary: "229, 53%, 53%",
        info: "204, 86%, 53%",
        success: "141, 53%, 53%",
        warning: "48, 100%, 67%",
        danger: "348, 100%, 61%",
        dark: "0, 0%, 21%",
      };
      return colors[color] || colors.primary;
    },
    set_tab_index(index) {
      if (index >= 0 && index < this.tabList.length) {
        const tab = this.tabList[index];
        if (!tab.disabled) {
          this.activeTab = tab.name;
          this.$emit("tab-changed", tab);
        }
      }
    },
  },
};
</script>

<style scoped>
/* Contenedor principal */
.tabs-container {
  position: relative;
}

/* Contenido de pestañas */
.tab-content {
  padding: 1.5rem;
  border-top: none;
  background: transparent;
  border-radius: 0 0 6px 6px;
}

.tab-panel {
  width: 100%;
}

/* Mejoras visuales para pestañas horizontales */
.tabs-horizontal .tabs {
  margin-bottom: 0;
}

.tabs-horizontal .tabs ul {
  border-bottom: 3px solid #3273dc;
  display: flex;
  width: 100%;
}

.tabs-horizontal .tabs ul li {
  margin-right: 0.5rem;
  flex: 1;
  padding-bottom: 1px;
}

.tabs-horizontal .tabs li a {
  border-bottom: 3px solid transparent;
  padding: 0.75rem 1.25rem;
  font-weight: 500;
  background-color: rgb(50, 50, 50);
  color: #757575;
  border-radius: 6px 6px 0 0;
  position: relative;
  overflow: hidden;
  width: 100%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tabs-horizontal .tabs li a:hover {
  background-color: rgba(116, 116, 116, 0.5);
  color: #bcbcbc;
  /* border-bottom-color: #3273dc; */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.tabs-horizontal .tabs li.is-active a {
  background-color: #3273dc;
  border-bottom-color: #00d1b2;
  color: #ffffff;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.tabs-horizontal .tabs li.is-disabled a {
  color: #b5b5b5;
  cursor: not-allowed;
  opacity: 0.6;
}

.tabs-horizontal .tabs li.is-disabled a:hover {
  background-color: transparent;
  box-shadow: none;
}

/* Mejoras para pestañas verticales */
.tabs-vertical {
  display: flex;
  gap: 1.5rem;
  min-height: 300px;
}

.tabs-vertical .tabs {
  flex: 0 0 280px;
  min-width: 280px;
}

.tabs-vertical .tabs ul {
  flex-direction: column;
  border-bottom: none;
  border-right: 2px solid #e8e8e8;
  padding-right: 1rem;
}

.tabs-vertical .tabs li {
  display: block;
  margin-bottom: 0.5rem;
  width: 100%;
}

.tabs-vertical .tabs li a {
  border-bottom: 1px solid transparent;
  border-right: 1px solid transparent;
  border-left: 4px solid transparent;
  border-radius: 6px;
  padding: 1rem 1.25rem;
  font-weight: 500;
  background-color: rgb(96, 96, 96);
  color: #f4f0f0;
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: flex-start;
}

.tabs-vertical .tabs li a:hover {
  background-color: rgba(245, 245, 245, 0.5);
  color: #d3d3d3;
  border-left-color: #dbdbdb;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.1);
}

.tabs-vertical .tabs li.is-active a {
  border-left-color: #3273dc;
  border-left-width: 4px;
  background-color: #3273dc;
  color: #ffffff;
  font-weight: 600;
  box-shadow: 2px 2px 8px rgba(0, 0, 0, 0.1);
}

.tabs-vertical .tabs li.is-disabled a {
  color: #b5b5b5;
  cursor: not-allowed;
  opacity: 0.6;
}

.tabs-vertical .tabs li.is-disabled a:hover {
  background-color: transparent;
  box-shadow: none;
}

.tabs-vertical .tab-content {
  flex: 1;
  border-left: none;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Iconos y badges */
.tabs li a .icon {
  margin-right: 0.5rem;
}

.tabs li a .tag {
  font-size: 0.75rem;
  height: 1.5em;
  padding-left: 0.5em;
  padding-right: 0.5em;
}

/* Responsive design */
@media screen and (max-width: 768px) {
  .tabs-vertical {
    flex-direction: column;
    gap: 1rem;
  }

  .tabs-vertical .tabs {
    flex: none;
    min-width: auto;
    width: 100%;
  }

  .tabs-vertical .tabs ul {
    flex-direction: row;
    border-right: none;
    border-bottom: 2px solid #e8e8e8;
    padding-right: 0;
    padding-bottom: 1rem;
    overflow-x: auto;
    white-space: nowrap;
  }

  .tabs-vertical .tabs li {
    display: inline-block;
    margin-right: 0.5rem;
    margin-bottom: 0;
    min-width: max-content;
  }

  .tabs-vertical .tabs li a {
    border-left: 4px solid transparent;
    border-bottom: 4px solid transparent;
    padding: 0.75rem 1rem;
  }

  .tabs-vertical .tabs li.is-active a {
    border-left-color: transparent;
    border-bottom-color: #3273dc;
    background-color: rgba(240, 247, 255, 0.8);
  }

  .tabs-vertical .tab-content {
    border-radius: 6px;
  }
}

/* Tamaños */
.tabs.is-small li a {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

.tabs.is-medium li a {
  padding: 1rem 1.5rem;
  font-size: 1.125rem;
}

.tabs.is-large li a {
  padding: 1.25rem 2rem;
  font-size: 1.25rem;
}

/* Estados de hover mejorados */
.tabs li a {
  position: relative;
  z-index: 1;
}

/* Sombras y profundidad */
.tabs-container {
  filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.1));
}

.tab-content {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
</style>
